import axios, { AxiosRequestConfig } from 'axios';
import { API_URL } from '@/config/config';
import messageModule from '@/store/modules/common/message.module';
import { MessageType } from '@/config/enum';
import jwtService from '@/services/common/jwt.service';
import { StatusCodes } from 'http-status-codes';
import authModule from '@/store/modules/common/auth.module';

export class BackgroundService {
  private request: number = 0;
  private response: number = 0;
  private _axios;

  constructor() {
    this._axios = axios.create({
      baseURL: API_URL
    });
    this.initInterceptor();
  }

  public initInterceptor() {
    this.request = this._axios.interceptors.request.use(async config => {
      if (jwtService.getToken()) {
        this.updateToken(config, jwtService.getToken());
      } else {
        await authModule.logout();
      }
      return config;
    });
    this.response = this._axios.interceptors.response.use(
      async response => {
        return response;
      },
      async error => {
        if (
          error.response.data.code === StatusCodes.UNAUTHORIZED ||
          error.response.data.code === StatusCodes.FORBIDDEN
        ) {
          await authModule.logout();
          window.location.href = '/';
        } else {
          await Promise.all([
            messageModule.showMessage({
              message: error.response.data.message,
              type: MessageType.danger
            })
          ]);
        }
        throw error;
      }
    );
  }

  public updateToken(config: AxiosRequestConfig, token: string | null): void {
    config.headers.common['Authorization'] = `${token}`;
  }

  public async query(resource: string, params?: any): Promise<any> {
    return await this._axios.get(resource, { params });
  }

  public async get(resource: string, slug?: string): Promise<any> {
    return await this._axios.get(`${resource}/${slug}`);
  }

  public async post(resource: string, params?: any): Promise<any> {
    return await this._axios.post(`${resource}`, params);
  }

  public async patch(resource: string, params?: any): Promise<any> {
    return await this._axios.patch(`${resource}`, params);
  }

  public async postWithFormData(
    resource: string,
    formData?: FormData,
    header?: any
  ) {
    return await this._axios.post(`${resource}`, formData, header);
  }

  public async update(
    resource: string,
    slug?: string,
    params?: any
  ): Promise<any> {
    return await this._axios.put(`${resource}/${slug}`, params);
  }

  public async put(resource: string, params?: any): Promise<any> {
    return await this._axios.put(`${resource}`, params);
  }

  public async delete(resource: string): Promise<any> {
    return await this._axios.delete(resource);
  }
}

const $backgroundService = new BackgroundService();
export default $backgroundService;
